// fence.ts — the HOUSEHOLD fence, as CODE instead of a wish.
//
// 🔴 WHY THIS EXISTS. Chores' fence was PROMPT-ONLY: a `SCOPE` string asking the model to decline
// judging people, recommending rewards/punishments, or giving personal advice. Ledger row 12 wants
// a **pre-LLM** fence plus a battery at 0 leaks / 0 over-blocks, and §5c already proved the general
// point the hard way — *"PROMPTING DOES NOT HOLD: the prompt was given an explicit rule and the very
// next generation ignored it twice."* A guardrail you cannot test is not a guardrail.
//
// It got sharper teeth this session for a second reason: the fence's wording was REWRITTEN
// (FAMILY/CHILDREN → HOUSEHOLD, "the child" → "the person"). A reworded guardrail is an untested
// guardrail until something proves it still holds.
//
// WHAT IT MUST DO — both directions matter equally:
//   REFUSE  judging a person · calling a count good/bad/enough · recommending reward or punishment ·
//           parenting / discipline / motivation / personal advice
//   ALLOW   everything about RECORDING and READING the log — add, log, edit, delete, search, counts,
//           points, streaks, "how many did Sam do last week". Over-blocking is a REAL failure: an
//           app that refuses "how many chores last week?" is broken, not safe.
//
// Deliberately zero-dependency and pure so `fence-battery.mjs` can run it in node with no browser,
// no network and no model.

export interface FenceVerdict {
  /** true = do not send to the model; answer with `message` instead. */
  refuse: boolean;
  /** which rule fired — for the battery's output, not shown to the user */
  rule?: "judge_person" | "rate_count" | "reward_punish" | "personal_advice";
  /** the plain one-sentence decline, pointing back at the figures */
  message?: string;
}

const OK: FenceVerdict = { refuse: false };

const DECLINE = {
  judge_person:
    "I only report what's been logged — I can't say how someone is doing as a person. I can tell you their counts, points and streak instead.",
  rate_count:
    "I can't say whether a number is good, bad or enough — that's a judgement. I can tell you exactly what was logged and how it compares to earlier weeks.",
  reward_punish:
    "I can't recommend rewards or consequences. I can show you who logged what, and the points each chore is worth.",
  personal_advice:
    "That's outside what I do — I keep to the chore log. I can pull up counts, points, streaks or what's overdue.",
} as const;

/** A person-ish subject: a name, a pronoun, or a household word. Kept broad on purpose. */
const SUBJECT = String.raw`(?:\b(?:he|she|they|him|her|them|his|their|my|our)\b[\w\s]{0,14}|\b[A-Z][a-z]+\b|\banyone\b|\beveryone\b|\bthe (?:kids?|children|child|family|household|others?)\b)`;

/** Words that turn a question about a PERSON into a judgement of them. */
const JUDGE_WORD = String.raw`lazy|useless|hopeless|bad|good|great|terrible|awful|slacking|slacker|freeloader|selfish|difficult|naughty|spoil(?:ed|t)|behaving|behaviour|behavior|well[- ]behaved|a good (?:kid|child|partner|housemate|person)|doing (?:well|badly|ok|okay|fine|great|poorly)|better than|worse than|the best|the worst|fair share|pulling (?:their|his|her) weight|carrying`;

/** "Is N enough / good?" — rating the NUMBER rather than reporting it. */
const RATE_COUNT = new RegExp(
  String.raw`\b(?:is|are|was|were|does|do|isn't|aren't)\b[^?]{0,40}\b(?:enough|too (?:few|little|many|much|low|high)|acceptable|normal|average|reasonable|respectable|impressive|good|bad|poor|low|high)\b`,
  "i",
);

const REWARD_PUNISH = new RegExp(
  String.raw`\b(?:should|shall|do you think|would you|can i|ought)\b[^?]{0,60}\b(?:reward|prize|treat|bonus|allowance|pocket money|bribe|incentiv\w*|punish\w*|ground(?:ed|ing)?|consequence|take away|confiscat\w*|withhold|dock|penalis\w*|penaliz\w*)\b` +
    String.raw`|\b(?:reward|punish\w*|ground|discipline)\s+(?:him|her|them|my|the)\b` +
    String.raw`|\bwhat(?:'s| is) a (?:good|fair) (?:reward|punishment|consequence)\b`,
  "i",
);

const PERSONAL_ADVICE = new RegExp(
  String.raw`\b(?:how (?:do|can|should) i (?:get|make|convince|motivate|discipline|teach|handle|deal with)|` +
    String.raw`what should i do about|any (?:advice|tips) (?:on|for|about)|how to (?:motivate|discipline|punish|reward)|` +
    String.raw`parenting|co[- ]?parent\w*|marriage|relationship advice|therap\w*|diagnos\w*|adhd|autis\w*|depress\w*|anxiet\w*)\b`,
  "i",
);

const JUDGE_PERSON = new RegExp(
  String.raw`(?:\b(?:is|are|was|were|isn't|aren't|do you think)\b[^?]{0,50}\b(?:${JUDGE_WORD})\b)` +
    String.raw`|(?:\b(?:${JUDGE_WORD})\b[^?]{0,30}${SUBJECT})` +
    String.raw`|(?:${SUBJECT}[^?]{0,30}\b(?:${JUDGE_WORD})\b)`,
  "i",
);

/**
 * Check a user message BEFORE it reaches the model.
 *
 * Order matters: the reward/punishment and advice rules are the most specific, so they run first —
 * "should I reward him for doing well?" must report as reward_punish, not judge_person.
 */
export function fenceCheck(text: string): FenceVerdict {
  const t = (text || "").trim();
  if (!t) return OK;

  if (REWARD_PUNISH.test(t)) return { refuse: true, rule: "reward_punish", message: DECLINE.reward_punish };
  if (PERSONAL_ADVICE.test(t)) return { refuse: true, rule: "personal_advice", message: DECLINE.personal_advice };
  if (RATE_COUNT.test(t)) return { refuse: true, rule: "rate_count", message: DECLINE.rate_count };
  if (JUDGE_PERSON.test(t)) return { refuse: true, rule: "judge_person", message: DECLINE.judge_person };
  return OK;
}
